// src/components/common/GroomingActivitySummaryDrawer.jsx
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiFileText,
  FiImage,
  FiCalendar,
  FiClock,
} from "react-icons/fi";
import { FaUserTie } from "react-icons/fa";
import { httpService } from "../../services/httpService";
import config from "../../config";

const s3Base = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || "";
const toDisplayUrl = (key) => {
  if (!key) return "";
  return key.startsWith("http") ? key : `${s3Base}/${key}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// Horizontal gallery-style photo strip used in Before/After timeline blocks
const EntryPhotosAndNote = ({ photos = [], note, onPreviewPhoto }) => {
  if (!photos.length && !note) {
    return <p className="text-sm text-slate-400 italic">Nothing recorded.</p>;
  }
  return (
    <>
      {photos.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2 mb-3 snap-x snap-mandatory scrollbar-thin">
          {photos.map((photo, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onPreviewPhoto(toDisplayUrl(photo))}
              className="shrink-0 snap-start w-28 h-28 rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:ring-2 hover:ring-indigo-300 transition-all"
            >
              <img
                src={toDisplayUrl(photo)}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
      {note && (
        <p className="text-sm text-slate-600 leading-relaxed">{note}</p>
      )}
    </>
  );
};

const GroomingActivitySummaryDrawer = ({
  refId,
  serviceType = "grooming",
  serviceName,
  dateLabel,
  staffName,
  petName,
  orderNumber,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [checkinNotes, setCheckinNotes] = useState("");
  const [activities, setActivities] = useState([]);
  const [activeTab, setActiveTab] = useState("timeline");
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const fetchSummary = async () => {
    setIsLoading(true);
    setError("");
    try {
      const [checkinRes, activitiesRes] = await Promise.allSettled([
        httpService.get(`/checkin/${serviceType}/${refId}`),
        httpService.get(`/serviceactivity/${serviceType}/${refId}/activities`),
      ]);
      const checkinData =
        checkinRes.status === "fulfilled" ? checkinRes.value?.data?.checkin_data : null;
      const activitiesRaw =
        activitiesRes.status === "fulfilled" ? activitiesRes.value?.data?.logs ?? [] : [];

      const sorted = Array.isArray(activitiesRaw)
        ? [...activitiesRaw].sort((a, b) => new Date(a.logged_at) - new Date(b.logged_at))
        : [];

      setCheckinPhotos(checkinData?.photos || []);
      setCheckinNotes(checkinData?.notes || "");
      setActivities(sorted);
      setHasFetched(true);
    } catch (err) {
      console.error("Failed to fetch grooming summary:", err);
      setError("Unable to load grooming summary.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (!hasFetched) fetchSummary();
  };

  const summary = useMemo(() => {
    const hasCheckin = checkinPhotos.length > 0 || !!checkinNotes;

    const activityPhotosCount = activities.reduce(
      (total, a) => total + (a?.photos?.length || 0),
      0,
    );
    const totalPhotos = checkinPhotos.length + activityPhotosCount;

    const activityNotesCount = activities.filter(
      (a) => a?.description && a.description.trim() !== "",
    ).length;
    const totalNotes = (checkinNotes ? 1 : 0) + activityNotesCount;

    const notesList = [
      ...(checkinNotes
        ? [{ date: dateLabel, stage: "Check-in", note: checkinNotes }]
        : []),
      ...activities
        .filter((a) => a?.description && a.description.trim() !== "")
        .map((a) => ({
          date: formatDateTime(a.logged_at) || dateLabel,
          stage: "Activity Log",
          note: a.description,
        })),
    ];

    const photosList = [
      ...checkinPhotos.map((url) => ({
        url,
        stage: "Check-in",
        date: dateLabel,
      })),
      ...activities.flatMap((a) =>
        (a.photos || []).map((url) => ({
          url,
          stage: "Activity Log",
          date: formatDateTime(a.logged_at) || dateLabel,
          note: a.description,
        })),
      ),
    ];

    return {
      hasCheckin,
      totalPhotos,
      totalNotes,
      activitiesCount: activities.length,
      notesList,
      photosList,
    };
  }, [checkinPhotos, checkinNotes, activities, dateLabel]);

  if (!refId) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
      >
        <FiFileText className="w-3.5 h-3.5" />
        View Summary
      </button>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <div
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-slate-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
              {/* Header */}
              <div className="bg-white border-b border-slate-200 px-6 py-2 shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                        Grooming Summary
                      </p>
                      <h2 className="text-xl font-bold text-slate-900">
                        {petName || "Pet"}
                      </h2>
                      {serviceName && (
                        <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md text-xs font-medium">
                          {serviceName}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 mt-1.5">
                      {dateLabel && (
                        <span className="flex items-center gap-1">
                          <FiCalendar className="w-3.5 h-3.5" /> {dateLabel}
                        </span>
                      )}
                      {staffName && (
                        <span className="flex items-center gap-1">
                          <FaUserTie className="w-3.5 h-3.5" /> {staffName}
                        </span>
                      )}
                      {orderNumber && (
                        <span className="bg-slate-100 px-2 py-0.5 rounded-md text-xs font-medium">
                          Order #{orderNumber}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
                    >
                      Share
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-2 md:p-2">
                {isLoading && (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-sm text-slate-500 flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      Loading grooming summary...
                    </div>
                  </div>
                )}

                {!isLoading && error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {!isLoading && !error && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-2">
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Check-in Status
                        </p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          {summary.hasCheckin ? "Captured" : " "}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          {checkinPhotos.length > 0
                            ? `${checkinPhotos.length} photo${checkinPhotos.length > 1 ? "s" : ""} at check-in`
                            : "No check-in photos"}
                        </p>
                      </div>

                      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Activity Log Entries
                        </p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">
                          {summary.activitiesCount}
                        </p>
                        <p className="text-sm text-slate-500 mt-3">
                          Logged during the service
                        </p>
                      </div>

                      {summary.totalPhotos > 0 && (
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Photos Captured
                          </p>
                          <p className="text-3xl font-bold text-slate-900 mt-3">
                            {summary.totalPhotos}
                          </p>
                          <p className="text-sm text-slate-500 mt-3">
                            Across check-in & activity log
                          </p>
                        </div>
                      )}

                      {summary.totalNotes > 0 && (
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Notes Recorded
                          </p>
                          <p className="text-3xl font-bold text-slate-900 mt-3">
                            {summary.totalNotes}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            Written during the service
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Tabs */}
                    <div className="flex space-x-1 bg-slate-200/50 p-1 rounded-xl w-max mb-2">
                      <button
                        onClick={() => setActiveTab("timeline")}
                        className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                          activeTab === "timeline"
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                        }`}
                      >
                        <FiClock className="w-4 h-4" />
                        Timeline
                      </button>

                      {summary.notesList.length > 0 && (
                        <button
                          onClick={() => setActiveTab("notes")}
                          className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                            activeTab === "notes"
                              ? "bg-white text-indigo-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                          }`}
                        >
                          <FiFileText className="w-4 h-4" />
                          Notes
                          <span className="ml-1.5 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                            {summary.notesList.length}
                          </span>
                        </button>
                      )}

                      {summary.photosList.length > 0 && (
                        <button
                          onClick={() => setActiveTab("photos")}
                          className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                            activeTab === "photos"
                              ? "bg-white text-indigo-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                          }`}
                        >
                          <FiImage className="w-4 h-4" />
                          Photos
                          <span className="ml-1.5 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                            {summary.photosList.length}
                          </span>
                        </button>
                      )}
                    </div>

                    {/* TAB: Timeline */}
                    {activeTab === "timeline" && (
                      <div className="animate-in fade-in duration-300 space-y-2">
                        {/* Before - Check-in */}
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                          <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
                              1
                            </span>
                            <p className="text-sm font-semibold text-slate-700">
                              Before — Check-in
                            </p>
                          </div>
                          <div className="p-5">
                            <EntryPhotosAndNote
                              photos={checkinPhotos}
                              note={checkinNotes}
                              onPreviewPhoto={setPreviewPhoto}
                            />
                          </div>
                        </div>

                        {/* After - Activity Log */}
<div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
  <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2">
    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
      2
    </span>
    <p className="text-sm font-semibold text-slate-700">
      After — Activity Log
    </p>
  </div>
  <div className="p-5">
    {activities.length === 0 ? (
      <p className="text-sm text-slate-400 italic">
        No activity log entries yet.
      </p>
    ) : (
      <div className="relative">
        {/* vertical connector line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
        <div className="space-y-6">
          {activities.map((a) => (
            <div key={a._id} className="relative pl-7">
              {/* timeline dot */}
              <span className="absolute left-0 top-1 w-3.5 h-3.5 rounded-full bg-indigo-500 ring-4 ring-indigo-100" />
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-400 mb-2">
                <FiClock className="w-3.5 h-3.5" />
                {formatDateTime(a.logged_at)}
              </div>
              <EntryPhotosAndNote
                photos={a.photos}
                note={a.description}
                onPreviewPhoto={setPreviewPhoto}
              />
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
</div>
                      </div>
                    )}

                    {/* TAB: Notes */}
                    {activeTab === "notes" && (
                      <div className="animate-in fade-in duration-300">
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                          <div className="overflow-x-auto">
                            <table className="w-full table-fixed">
                              <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">
                                    Date
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">
                                    Stage
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                    Note
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {summary.notesList.map((n, idx) => (
                                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap">
                                      {n.date}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                        {n.stage}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-700">
                                      <div className="whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                                        {n.note}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB: Photos */}
                    {activeTab === "photos" && (
                      <div className="animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {summary.photosList.map((photo, idx) => (
                            <div
                              key={idx}
                              className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-100 cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => setPreviewPhoto(toDisplayUrl(photo.url))}
                            >
                              <img
                                src={toDisplayUrl(photo.url)}
                                alt=""
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent p-3 pt-10 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                                <p className="text-white text-xs font-bold leading-tight">
                                  {photo.stage}
                                </p>
                                <p className="text-slate-300 text-[10px] mt-0.5">
                                  {photo.date}
                                </p>
                                {photo.note && (
                                  <p className="text-slate-300 text-[10px] mt-0.5 line-clamp-1">
                                    📝 {photo.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Photo preview */}
      {previewPhoto &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-6"
            onClick={() => setPreviewPhoto(null)}
          >
            <img
              src={previewPhoto}
              alt=""
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setPreviewPhoto(null)}
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

export default GroomingActivitySummaryDrawer;